import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  listIqServices,
  getIqService,
  createIqService,
  updateIqService,
  deleteIqService,
  testIqService,
  listIqServiceServices,
} from '@/lib/api/iq-services';
import { ApiError } from '@/lib/api/client';
import type {
  IqServiceDetail,
  IqServiceListResponse,
  IqServiceTestResult,
  IqServiceServicesResponse,
  CreateIqServiceRequest,
  UpdateIqServiceRequest,
} from '@/types/iq-service';

export const iqServicesKeys = {
  all: ['iq-services'] as const,
  lists: () => [...iqServicesKeys.all, 'list'] as const,
  details: () => [...iqServicesKeys.all, 'detail'] as const,
  detail: (id: string) => [...iqServicesKeys.details(), id] as const,
  services: (id: string) => [...iqServicesKeys.detail(id), 'services'] as const,
};

export function useIqServices() {
  return useQuery<IqServiceListResponse, Error>({
    queryKey: iqServicesKeys.lists(),
    queryFn: listIqServices,
    staleTime: 60_000,
  });
}

export function useIqService(id: string | undefined) {
  return useQuery<IqServiceDetail, Error>({
    queryKey: iqServicesKeys.detail(id ?? ''),
    queryFn: () => getIqService(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useIqServiceServices(id: string | undefined) {
  return useQuery<IqServiceServicesResponse, Error>({
    queryKey: iqServicesKeys.services(id ?? ''),
    queryFn: () => listIqServiceServices(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateIqService() {
  const queryClient = useQueryClient();
  return useMutation<IqServiceDetail, Error, CreateIqServiceRequest>({
    mutationFn: (data) => createIqService(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: iqServicesKeys.lists() });
    },
  });
}

export function useUpdateIqService() {
  const queryClient = useQueryClient();
  return useMutation<
    IqServiceDetail,
    Error,
    { id: string; data: UpdateIqServiceRequest }
  >({
    mutationFn: ({ id, data }) => updateIqService(id, data),
    onSuccess: (updated) => {
      queryClient.setQueryData(iqServicesKeys.detail(updated.iqServiceId), updated);
      queryClient.invalidateQueries({ queryKey: iqServicesKeys.lists() });
    },
    onError: (error, variables) => {
      // M-4b: Optimistic Locking 409 Handling
      if (error instanceof ApiError && error.status === 409) {
        toast.error('Daten wurden zwischenzeitlich geaendert — bitte neu laden');
        queryClient.invalidateQueries({ queryKey: iqServicesKeys.detail(variables.id) });
      }
    },
  });
}

export function useDeleteIqService() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteIqService(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: iqServicesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: iqServicesKeys.lists() });
    },
  });
}

export function useTestIqService() {
  return useMutation<IqServiceTestResult, Error, string>({
    mutationFn: (id) => testIqService(id),
  });
}
